import { IconButton, IStackTokens, Stack, Text } from '@fluentui/react';
import React, { useEffect, useRef, useState } from 'react';
import { Guid } from '../../../Common/models/Guid';
import { Release } from '../../models/Release';
import { ReleaseStage } from '../../models/ReleaseStage';
import { DetailsPanel } from './Display/DetailsPanel';
import { Deployment } from '../../models/Deployment';
import { ReleaseStageDetails } from './Display/ReleaseStageDetails';
import {
    clearOpenStatusFlyoutInSessionStorage,
    getOpenStatusFlyoutFromSessionStorage,
    setOpenStatusFlyoutInSessionStorage,
} from '../../../Common/util/localStorageUtils';
import { Build } from '../../models/Build';
import { useIsMobile } from '../../../Common/hooks/useIsMobile';
import { MobileReleaseStageDetails } from './Display/Mobile/MobileReleaseStageDetails';
import { MetadataDetailsPanel } from './MetadataDetailsPanel';
import { ReleaseCloudHelp } from './Display/ReleaseCloudHelp';
import {
    getStageDependencyForest,
    getStageDependencyPath,
    StageDependencyNode,
} from '../../util/StageDependencyUtils';

interface IReleaseFlow {
    release: Release | undefined;
    isLoaded: boolean;
    serviceId?: Guid;
    onRefreshReleaseData?: () => void;
}

export const ReleaseFlow: React.FC<IReleaseFlow> = (props: IReleaseFlow) => {
    const sectionStackTokens: IStackTokens = { childrenGap: 20 };
    const [stages, setStages] = useState<ReleaseStage[]>([]);
    const [selected, setSelected] = useState<Deployment | Build | undefined>();
    const [highlightedEntity, setHighlightedEntity] = useState<
        Deployment | Build
    >();
    const [navigationTargetIdentifier, setNavigationTargetIdentifier] =
        useState<string>();
    const [
        navigationTargetStageIdentifier,
        setNavigationTargetStageIdentifier,
    ] = useState<string>();
    const [isPanelOpen, setIsPanelOpen] = useState<boolean>(false);
    const [isMetadataPanelOpen, setIsMetadataPanelOpen] =
        useState<boolean>(false);
    const [collapsedState, setCollapsedState] = useState<{
        [key: string]: boolean;
    }>({});

    const showPanel = () => setIsPanelOpen(true);
    const selectEntity: React.Dispatch<
        React.SetStateAction<Deployment | Build | undefined>
    > = (value) => {
        if (typeof value !== 'function') {
            setHighlightedEntity(value);
        }
        setSelected(value);
    };
    const hidePanel = () => {
        setIsPanelOpen(false);
        setHighlightedEntity(undefined);
    };
    const showMetadataPanel = () => setIsMetadataPanelOpen(true);
    const hideMetadataPanel = () => setIsMetadataPanelOpen(false);
    const isMobile = useIsMobile();

    const getStageForItem = (itemOrIdentifier: Deployment | Build | string) =>
        stages.find(
            (stage) =>
                stage.deployments?.some((deployment) =>
                    typeof itemOrIdentifier === 'string'
                        ? deployment.uniqueIdentifier === itemOrIdentifier
                        : deployment === itemOrIdentifier
                ) ||
                stage.builds?.some((build) =>
                    typeof itemOrIdentifier === 'string'
                        ? build.uniqueIdentifier === itemOrIdentifier
                        : build === itemOrIdentifier
                )
        );

    const resolveDependencyItem = (
        sourceItem: Deployment | Build,
        dependencyIdentifier: string,
        dependencyType: 'Build' | 'Deployment'
    ) => {
        const sourceStage = getStageForItem(sourceItem);
        if (dependencyType === 'Build') {
            return sourceStage?.builds?.find(
                (build) => build.uniqueIdentifier === dependencyIdentifier
            );
        }

        return stages
            .flatMap((stage) => stage.deployments ?? [])
            .find(
                (deployment) =>
                    deployment.uniqueIdentifier === dependencyIdentifier
            );
    };

    const navigateToDependency = (
        sourceIdentifier: string,
        destinationIdentifier: string,
        destinationEntity?: Deployment | Build
    ) => {
        const sourceStage = getStageForItem(sourceIdentifier);
        const destinationStage = destinationEntity
            ? stages.find(
                  (stage) =>
                      stage.deployments?.includes(
                          destinationEntity as Deployment
                      ) || stage.builds?.includes(destinationEntity as Build)
              )
            : getStageForItem(destinationIdentifier);
        if (!sourceStage || !destinationStage) {
            return;
        }

        if (destinationStage.environment) {
            const environmentStages = stages.filter(
                (stage) => stage.environment === destinationStage.environment
            );
            const dependencyPath = getStageDependencyPath(
                environmentStages,
                destinationStage
            );
            setCollapsedState((currentState) => ({
                ...currentState,
                [destinationStage.environment as string]: false,
                ...(dependencyPath ?? []).reduce(
                    (expandedGroups, stage) => ({
                        ...expandedGroups,
                        [`dependency-${stage.releaseStageCorrelationId}`]:
                            false,
                    }),
                    {}
                ),
            }));
        }
        setNavigationTargetStageIdentifier(
            destinationStage.releaseStageCorrelationId
        );
        setNavigationTargetIdentifier(destinationIdentifier);
    };

    useEffect(() => {
        if (selected?.uniqueIdentifier) {
            setHighlightedEntity(selected);
        }
    }, [selected]);

    // When parent component sends new data, refresh my state accordingly
    useEffect(() => {
        if (props.release && props.release.stages) {
            // Stages should be sorted before being grouped
            const sortedStagesByEnvironment = props.release.stages.sort(
                (s1, s2) => {
                    if (s1.environment && s2.environment) {
                        return s1.environment > s2.environment ? -1 : 1;
                    }

                    return 0;
                }
            );

            setStages(sortedStagesByEnvironment);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.release, props.isLoaded]);

    // Keep the open flyout's data current after an in-place (auto) refresh. The
    // `selected` object still references the previous data, so re-map it to the
    // freshly loaded object with the same uniqueIdentifier whenever stages
    // change. If the item no longer exists, close the flyout.
    useEffect(() => {
        if (
            !isPanelOpen ||
            !selected?.uniqueIdentifier ||
            stages.length === 0
        ) {
            return;
        }
        let match: Deployment | Build | undefined;
        for (const stage of stages) {
            match =
                stage.deployments?.find(
                    (d) => d.uniqueIdentifier === selected.uniqueIdentifier
                ) ??
                stage.builds?.find(
                    (b) => b.uniqueIdentifier === selected.uniqueIdentifier
                );
            if (match) {
                break;
            }
        }

        if (match) {
            if (match !== selected) {
                setSelected(match);
            }
        } else {
            // Selected item no longer exists after refresh; close flyout and clear saved state
            setSelected(undefined);
            setIsPanelOpen(false);
            clearOpenStatusFlyoutInSessionStorage();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stages]);

    // Reopen the build/deployment status flyout after a full page reload (e.g.
    // a manual browser refresh). We only restore once, after the release data
    // has loaded, by matching the saved uniqueIdentifier against the freshly
    // loaded stages so the flyout shows up-to-date data.
    const hasRestoredFlyout = useRef<boolean>(false);

    useEffect(() => {
        if (hasRestoredFlyout.current) {
            return;
        }
        const releaseCorrelationId = props.release?.releaseCorrelationId;
        if (!props.isLoaded || stages.length === 0 || !releaseCorrelationId) {
            return;
        }

        const saved = getOpenStatusFlyoutFromSessionStorage();
        if (saved && saved.releaseCorrelationId === releaseCorrelationId) {
            let match: Deployment | Build | undefined;
            for (const stage of stages) {
                match =
                    stage.deployments?.find(
                        (d) => d.uniqueIdentifier === saved.uniqueIdentifier
                    ) ??
                    stage.builds?.find(
                        (b) => b.uniqueIdentifier === saved.uniqueIdentifier
                    );
                if (match) {
                    break;
                }
            }
            if (match) {
                setSelected(match);
                setIsPanelOpen(true);
            }
        }
        hasRestoredFlyout.current = true;
    }, [props.isLoaded, stages, props.release?.releaseCorrelationId]);

    // Remember whether the status flyout is open (and for which item) so it can
    // be reopened after an auto-refresh reload. Guarded on the restore having
    // run first so the saved value is not cleared before it is read.
    useEffect(() => {
        if (!hasRestoredFlyout.current) {
            return;
        }
        const releaseCorrelationId = props.release?.releaseCorrelationId;
        if (!releaseCorrelationId) {
            return;
        }
        if (isPanelOpen && selected?.uniqueIdentifier) {
            setOpenStatusFlyoutInSessionStorage(
                releaseCorrelationId,
                selected.uniqueIdentifier
            );
        } else {
            clearOpenStatusFlyoutInSessionStorage();
        }
    }, [isPanelOpen, selected, props.release?.releaseCorrelationId]);

    // Toggle collapse for a specific cloud
    const toggleCollapse = (cloud: string) => {
        setCollapsedState((prevState) => ({
            ...prevState,
            [cloud]: !prevState[cloud],
        }));
    };

    type GroupedClouds = { [key: string]: ReleaseStage[] };

    const groupedItems: GroupedClouds = stages.reduce((acc, item) => {
        const { environment } = item;

        if (environment) {
            if (!acc[environment]) {
                acc[environment] = [];
            }
            acc[environment].push(item);
            return acc;
        }

        return acc;
    }, {} as GroupedClouds);

    const clearNavigationTarget = () => {
        setNavigationTargetIdentifier(undefined);
        setNavigationTargetStageIdentifier(undefined);
    };

    const renderStage = (stage: ReleaseStage) =>
        isMobile ? (
            <MobileReleaseStageDetails
                key={stage.releaseStageCorrelationId}
                isLoaded={props.isLoaded}
                stage={stage}
                setSelected={selectEntity}
                showPanel={showPanel}
                showMetadataPanel={showMetadataPanel}
                isPanelOpen={stages.length === 1}
                pipelineSource={props.release?.releasePipelineSource}
                serviceTreeId={props.release?.serviceTreeId}
                navigationTargetIdentifier={navigationTargetIdentifier}
                navigationTargetStageIdentifier={
                    navigationTargetStageIdentifier
                }
                onNavigationComplete={clearNavigationTarget}
            />
        ) : (
            <ReleaseStageDetails
                key={stage.releaseStageCorrelationId}
                isLoaded={props.isLoaded}
                stage={stage}
                setSelected={selectEntity}
                showPanel={showPanel}
                isPanelOpen={stages.length === 1}
                pipelineSource={props.release?.releasePipelineSource}
                onRefreshReleaseData={props.onRefreshReleaseData}
                serviceTreeId={props.release?.serviceTreeId}
                highlightedEntity={highlightedEntity}
                navigationTargetIdentifier={navigationTargetIdentifier}
                navigationTargetStageIdentifier={
                    navigationTargetStageIdentifier
                }
                onNavigationComplete={clearNavigationTarget}
            />
        );

    // Dependency order is applied, but nesting is not surfaced beyond ordering.
    const renderStageGroup = (node: StageDependencyNode): React.ReactNode => (
        <React.Fragment key={node.stage.releaseStageCorrelationId}>
            {renderStage(node.stage)}
            {node.children.map((child) => renderStageGroup(child))}
        </React.Fragment>
    );

    const renderCloudStages = (
        cloudStages: ReleaseStage[]
    ): React.ReactNode => {
        const forest = getStageDependencyForest(cloudStages);
        // Spacing separates dependency groups; a flat cloud reads better without it.
        const hasDependencies = forest.some((node) => node.children.length > 0);

        return forest.map((root, index) => (
            <div
                key={root.stage.releaseStageCorrelationId}
                style={
                    hasDependencies && index > 0
                        ? { paddingTop: 10 }
                        : undefined
                }
            >
                {renderStageGroup(root)}
            </div>
        ));
    };

    return (
        <div className="ReleaseFlow-root">
            <Stack horizontal tokens={sectionStackTokens}>
                {/* PageTitle with flow name, submitter, and timestamp */}
                <Stack
                    tokens={sectionStackTokens}
                    style={{
                        width: '100%',
                    }}
                >
                    <div>
                        {Object.keys(groupedItems).map((cloud) => {
                            const isCollapsed = collapsedState[cloud] || false;
                            return (
                                <Stack key={cloud}>
                                    <Stack
                                        horizontal
                                        wrap
                                        verticalAlign="center"
                                        tokens={{ childrenGap: 8 }}
                                    >
                                        <IconButton
                                            iconProps={{
                                                iconName: isCollapsed
                                                    ? 'ChevronRight'
                                                    : 'ChevronDown',
                                            }}
                                            onClick={() =>
                                                toggleCollapse(cloud)
                                            }
                                            ariaLabel={`${
                                                isCollapsed
                                                    ? 'Expand'
                                                    : 'Collapse'
                                            } ${cloud} stages`}
                                            aria-expanded={!isCollapsed}
                                            title={
                                                isCollapsed
                                                    ? 'Expand'
                                                    : 'Collapse'
                                            }
                                        />
                                        <Text variant="xLarge">{`${cloud} (${groupedItems[cloud].length})`}</Text>
                                        <ReleaseCloudHelp
                                            cloudName={cloud}
                                            releaseCorrelationId={
                                                props.release
                                                    ?.releaseCorrelationId
                                            }
                                            releaseId={props.release?.releaseId}
                                            organizationName={
                                                props.release?.adoInstance ||
                                                props.release?.accountName
                                            }
                                            projectName={
                                                props.release?.projectName
                                            }
                                        />
                                    </Stack>

                                    {isCollapsed ? (
                                        <></>
                                    ) : (
                                        <div>
                                            {renderCloudStages(
                                                groupedItems[cloud]
                                            )}
                                        </div>
                                    )}
                                </Stack>
                            );
                        })}
                    </div>
                </Stack>
            </Stack>
            <DetailsPanel
                isOpen={isPanelOpen}
                dismissPanel={hidePanel}
                selected={selected}
                onDisplayedItemChange={setHighlightedEntity}
                onDependencyNavigate={navigateToDependency}
                resolveDependencyItem={resolveDependencyItem}
                resolveItemStageName={(item) =>
                    getStageForItem(item)?.stageName
                }
                availableItems={stages.flatMap((stage) => [
                    ...(stage.builds ?? []),
                    ...(stage.deployments ?? []),
                ])}
                isLoaded={props.isLoaded}
            />
            <MetadataDetailsPanel
                isOpen={isMetadataPanelOpen}
                dismissPanel={hideMetadataPanel}
                entity={selected}
                isLoaded={props.isLoaded}
            />
        </div>
    );
};
